import _ from 'lodash';
import { format as formatDate, parseISO } from 'date-fns';
import { DATASET_TYPES, DATASET_CONFIG, DATA_RANGE, DEFAULT_PRESET_ID_FOR_ALL_DATASETS } from '@awp/constants';
import { FROST_PROBE_CONFIG, SNOW_POLE_CONFIG } from '@awp/constants';

export const isValueEmpty = (v) => {
  if (v === null || v === undefined || v === '') return true;
  return false;
};
export const isObjectEmpty = (v) => {
  return _.isEmpty(v);
};
export const isValueNotEmpty = (v) => {
  if (v !== null && typeof v !== undefined && v !== '') return true;
  return false;
};
export const isMatchArray = (vArray, array) => {
  if (isValueEmpty(vArray) && isValueEmpty(array)) return true;
  if (vArray.length !== array.length) return false;
  if (vArray.length === 0 && array.length === 0) return true;
  for (var i = 0; i < vArray.length; ++i) {
    if (vArray[i] !== array[i]) return false;
  }
  return true;
};

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};
export const toNumberOrNull = (v) => {
  return isValueNotEmpty(removeStringCommas(v)) ? _.toNumber(removeStringCommas(v)) : null;
};
export const toStringOrEmpty = (v) => {
  return isValueNotEmpty(v) ? _.toString(v) : '';
};
export const toStringWithCommasOrEmpty = (v) => {
  return isValueNotEmpty(v) ? _.toString(addCommasToNumber(v)) : '';
};
export const removeStringCommas = (v) => {
  return v.toString().replace(/,/g, '');
};
export const addCommasToNumber = (n) => {
  if (isValueEmpty(n)) return n;
  let s = removeStringCommas(n).split('.');
  if (s[0].length >= 4) s[0] = s[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
  return s.join('.');
};
export const isValidDecimal = (v, digits) => {
  if (isValueEmpty(v)) return true;
  const d = _.toInteger(digits);
  let s = Number(removeStringCommas(v)).toString().split('.');
  if (s.length < 2) return true;
  if (s[1].length <= d) return true;
  return false;
};
export const isDecimal = (v, digits) => {
  if (isValueEmpty(v)) return false;
  if (isNaN(v)) return false;
  return isValidDecimal(v, digits);
};
export const isInt = (v) => {
  if (isValueEmpty(v)) return false;
  if (!isNaN(v)) return _.isInteger(_.toNumber(v));
  return false;
};

export const isIntStr = (v) => {
  if (isValueEmpty(v)) return false;
  v = (v + '').replace('-', '').replace('+', ''); //remove sign
  return !isNaN(v) && Number(v) == v;
};

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
/* istanbul ignore file */
export const debounce = (func, wait, immediate) => {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

export let isFunction = (obj) => {
  return obj && {}.toString.call(obj) === '[object Function]';
};
export const isOdd = (n) => {
  return Number(n) % 2 == 1;
};

//check if the json contains empty. e.g. [{}].length =1 but it is empty
export const isJsonValueEmpty = (j) => {
  return isValueEmpty(j) || JSON.stringify(j) === '[]' || JSON.stringify(j) === '[{}]' || JSON.stringify(j) === '{}';
};

//convert utc to local datetime string
export const getUtcToLocal = (tm) => {
  // tm format: e.g. 2022-02-19T13:30:00
  if (isValueEmpty(tm)) return '';
  if (!(tm + '').endsWith('Z')) tm = tm + 'Z';
  return formatDate(new Date(tm), 'yyyy-MM-dd HH:mm:ss');
};

//convert utc to local datetime string
export const getUtcToLocalByFormat = (tm, fm) => {
  // tm format: e.g. 2022-02-19T13:30:00
  if (isValueEmpty(tm)) return '';
  if (isValueEmpty(fm)) fm = 'yyyy-MM-dd HH:mm:ss';
  if (!(tm + '').endsWith('Z')) tm = tm + 'Z';
  return formatDate(new Date(tm), fm);
};

//convert utc to local datetime string
export const getLocalByFormat = (tm, fm) => {
  // tm format: e.g. 2022-02-19T13:30:00
  if (isValueEmpty(tm)) return '';
  if (isValueEmpty(fm)) fm = 'yyyy-MM-dd HH:mm:ss';
  return formatDate(new Date(tm), fm);
};
//convert utc(zulu or non-zulu) to local
// supports the below three date time formats
//e.g. 2022-09-28T02:28:00
//     2022-09-28T02:28:00Z
//     1674071247843
//provide two formats based on breakpoints
export const getUtcToLocalByBreakpoints = (
  value,
  breakpoints,
  fmt = 'yyyy-MM-dd HH:mm',
  sfmt = 'MMM dd yyyy HH:mm',
) => {
  if (isValueEmpty(value)) return '';
  let format = breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall ? fmt : sfmt;
  if (!(value + '').endsWith('Z') && !(value + '').includes('T')) return getLocalByFormat(value, format); // datetime e.g. 1674071247843
  return getUtcToLocalByFormat(value, format);
};

//convert local datetime to utc ISO string
export const convLocalToIsoString = (localDt) => {
  // localDt local date format: e.g. Tue Sep 27 2022 19:28:00 GMT-0700 (Pacific Daylight Time)
  //  					   or e.g. Tue Sep 27 2022 20:28:35 GMT-0600 (Mountain Daylight Time)
  // return date string: e.g. 2022-09-28T02:28:00Z (00 second)
  let dateIsoStr = localDt.toISOString();
  let [yyyy, mm, dd, hh, mi] = dateIsoStr.split(/[/:\-T]/);
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:00Z`;
};

//convert utc ISO datetime to api date parameter string
export const convUtcIsoToApiDateString = (utcDateTime, fm) => {
  // localDt local date format: e.g.2022-09-28T02:28:00
  //  					   or e.g. 2022-09-28T02:28:00Z
  // fm: string date format: e.g. yyyy-MM-dd-HH-mm or 'yyyy-MM-dd-HH-mm-ss
  // return date string: e.g. 2022-12-12-20-00 (00 minute)
  if (isValueEmpty(utcDateTime)) return '';
  if (isValueEmpty(fm)) fm = 'yyyy-MM-dd-HH-mm';
  return formatDate(new parseISO(utcDateTime), fm);
};

export const truncateString = (str, n) => {
  return str.length > n ? str.substr(0, n - 1) + '...' : str;
};

export const convertRelElevationTocm = (relElevation) => {
  if (!isValueEmpty(relElevation)) return relElevation / 10;
  return relElevation;
};
export const convertRelElevationTomm = (relElevation) => {
  if (!isValueEmpty(relElevation)) return relElevation * 10;
  return relElevation;
};
export const isValidRelElevation = (relElevation) => {
  if (isValueEmpty(relElevation)) return true;
  let re1 = (relElevation + '').replace('-', '').replace('+', ''); //remove sign
  if (isNaN(re1) || isValueEmpty(re1)) return false;
  let re2 = _.toNumber(re1) * 10;
  return _.isInteger(_.toNumber(re2));
};

export const isValidRelElevationDigit = (relElevation, decimalPlaces = 1) => {
  if (!isValidRelElevation(relElevation)) return false;
  return ((relElevation + '' || '').split('.')[1] || []).length <= decimalPlaces;
};

/**
 * Parses the datasetId and extracts the prefix, datasetType, and observationTypeId.
 *
 * @param {string} datasetId - The datasetId to be parsed.
 * @returns {Object} An object containing the parsed dataset information.
 * @property {number} prefix - The prefix extracted from the datasetId.
 * @property {string} datasetType - The dataset type determined based on the prefix.
 * @property {number} observationTypeId - The observation type id extracted from the datasetId.
 */
export const parseDatasetId = (datasetId) => {
  // Split the datasetId using underscore '_' as the delimiter.
  const attributes = datasetId.split('_');

  // Parse the prefix from the first attribute.
  const prefix = parseInt(attributes[0]);

  // Initialize datasetType variable to store the determined dataset type.
  let datasetType = '';

  // Determine the dataset type based on the parsed prefix.
  if (prefix === 0) datasetType = DATASET_TYPES.Regular;
  else if (prefix === 1) datasetType = DATASET_TYPES.Image;
  else if (prefix === 2) datasetType = DATASET_TYPES.ForecastData;
  else if (prefix >= 100000 && prefix < 200000) datasetType = DATASET_TYPES.FrostProbe;
  else if (prefix >= 200000 && prefix < 300000) datasetType = DATASET_TYPES.SnowPole;

  // Parse the observationTypeId from the second attribute.
  const observationTypeId = parseInt(attributes[1]);

  // Return an object containing the parsed dataset information.
  return { prefix, datasetType, observationTypeId };
};

/**
 * Sorts the data array based on the "SensorLayerNumber" and "nameSortProp" properties.
 * This function ensures stable sorting by preserving the original order of elements with equal sort values.
 *
 * @param {Array} data - The data array containing objects to be sorted.
 * @param {string} nameSortProp - The property name used for alphabetical sorting.
 * @param {boolean} sortByElevation - Toggle off to sort by SensorLayerNumber otherwise sorting will default to RelativeElevation
 * @returns {Array} - A new sorted array containing the objects sorted first by "SensorLayerNumber" and then alphabetically by "nameSortProp".
 */
export const sortFrostProbeTypeData = (data, nameSortProp, sortByElevation = true) => {
  // If the data array is empty or undefined, return it as is.
  if (isValueEmpty(data)) return data;

  const numericalSort = data.slice().sort((a, b) => {
    const aNumeric =
      a[sortByElevation ? 'RelativeElevation' : 'SensorLayerNumber'] !== null
        ? a[sortByElevation ? 'RelativeElevation' : 'SensorLayerNumber']
        : Number.POSITIVE_INFINITY;
    const bNumeric =
      b[sortByElevation ? 'RelativeElevation' : 'SensorLayerNumber'] !== null
        ? b[sortByElevation ? 'RelativeElevation' : 'SensorLayerNumber']
        : Number.POSITIVE_INFINITY;

    return Math.abs(aNumeric) - Math.abs(bNumeric);
  });

  // Sort alphabetically by nameSortProp (keeping the RelativeElevation order intact)
  const sortedData = numericalSort.sort((a, b) => {
    // Compare nameSortProp values and return the comparison result
    return a[nameSortProp] < b[nameSortProp] ? -1 : a[nameSortProp] > b[nameSortProp] ? 1 : 0;
  });

  // Return the sorted data array.
  return sortedData;
};

//FrostProbeType means frost probe and snow pole or more for future use relative elevation to sort
// the structure of data object is based on ObservationDTO
// all data must be frost probe or snow pole type
// data should RelativeElevation
export const sortAllFrostProbeTypeData = (data) => {
  if (isValueEmpty(data)) return data;
  let sortedData = data.sort((a, b) => {
    return (
      !isValueEmpty(a.RelativeElevation) &&
      !isValueEmpty(b.RelativeElevation) &&
      Math.abs(a.RelativeElevation) - Math.abs(b.RelativeElevation)
    );
  });

  return sortedData;
};

//FrostProbeType means frost probe and snow pole or more for future use relative elevation to sort
// the structure of data object is based on { value, label, group, typeId , rawElevation};
// data should have rawElevation
export const sortFrostProbeTypeDropDown = (data) => {
  if (isValueEmpty(data)) return data;
  let sortedData = data.sort((a, b) => {
    return (
      !isValueEmpty(a.rawElevation) &&
      !isValueEmpty(b.rawElevation) &&
      Math.abs(a.rawElevation) - Math.abs(b.rawElevation)
    );
  });

  return sortedData;
};
export const getFrostProbeTypeCombinedName = (displayName, relElevaton) => {
  if (isValueEmpty(relElevaton)) return displayName;
  return displayName + ' ' + relElevaton + ' cm';
};

//e.g.	'0.04 - OLD SLIDE'
//		return '0.04'
export const getLocationPrefix = (location, breakpoints) => {
  if (!location) return location;
  let l = location.split('-');
  if (!l || l.length < 1) return location;
  return breakpoints.isLarge || breakpoints.isMedium ? location : l[0].trim();
};

// location - from /to points
//e.g.	'10 Yale - Northbound double gates at Yale / 20 Spuzzum South - Southbound double gate at the south end of Spuzzum'
//		return '10 Yale / 20 Spuzzum South'
export const resizePointLocationName = (location, breakpoints) => {
  let lines = location.split('/');
  if (lines && lines.length === 2) {
    let from = lines[0].trim();
    let to = lines[1].trim();
    let l1 = from.split('-');
    let l2 = to.split('-');
    if (l1 && l2 && l1.length === 2 && l2.length === 2) {
      return breakpoints.isLarge || breakpoints.isMedium ? location : l1[0].trim() + ' / ' + l2[0].trim();
    }
  }
  return location;
};
export const isIos = () => {
  let result =
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
  return result;
};

export const getInitialTouched = (dto, value) => {
  if (!dto) return null;
  const initialTouched = {};
  for (var key in dto) {
    initialTouched[key] = value;
  }
  return initialTouched;
};
export const formatJson = (str) => {
  if (!str) return str;

  try {
    const obj = JSON.parse(str);
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    return str.trim();
  }
};

// lodash _.sortBy  is not compatible with IOS
//this function is a replacement
//usage: tableData.sort(fieldSorter(['TimeStamp','x2']))
export const fieldSorter = (fields) => (a, b) =>
  fields
    .map((o) => {
      let dir = 1;
      if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
      }
      return a[o] > b[o] ? dir : a[o] < b[o] ? -dir : 0;
    })
    .reduce((p, n) => (p ? p : n), 0);

//check if contains the below which could break ingestion:
// less than
// greater than
// ampersand
// apostrophe
// double - quote
export const containsSpecialChars = (str) => {
  const regex = /[<>&'"]/;
  return regex.test(str);
};

// check if timestamp comparing with Now is in the current Avlanche season [last year Aug 1- this year July 30]
export const isAvalancheSeason = (timestamp) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const august1 = new Date(currentYear, 7, 1); // August is the 8th month (index 7)

  if (now >= august1) {
    // After August 1 this year
    const nextYear = currentYear + 1;
    const august1NextYear = new Date(nextYear, 7, 1);
    return timestamp >= august1 && timestamp < august1NextYear;
  } else {
    // Before August 1 this year
    const lastYear = currentYear - 1;
    const august1LastYear = new Date(lastYear, 7, 1);
    return timestamp >= august1LastYear && timestamp < august1;
  }
};
export const getAvalancheSeason = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const august1 = new Date(currentYear, 7, 1); // August is the 8th month (index 7)
  if (now >= august1) {
    // After August 1 this year
    const nextYear = currentYear + 1;
    return new Date(nextYear, 7, 1);
  } else {
    // Before August 1 this year
    const lastYear = currentYear - 1;
    return new Date(lastYear, 7, 1);
  }
};
export const getAntiForgeryToken = () => {
  let token = document.querySelector('#antiForgeryForm input[name="__RequestVerificationToken"]');
  if (token) return token.value;
  return null;
};
//dataset - an element of data.DataVisualizationDTOs.Datasets
export const isWxnetDataset = (dataset) => {
  if (!dataset) return false;
  const _dataset = { ...dataset };

  return (
    _dataset.SensorTypeName === DATASET_CONFIG.WxNetPavement && _dataset.PlacementName === DATASET_CONFIG.WxNetPavement
  );
};
//dataset - an element of data.DataVisualizationDTOs
//option - an element of dataGraph.PersistentGraphFilters
//forecastWeatherStationId - selected station id in forecast panel
export const isSelectedWxnetDataset = (dataset, option, forecastWeatherStationId) => {
  if (!dataset || !option) return false;
  const _dataset = { ...dataset };
  const _option = { ...option };
  return (
    isWxnetDataset(_dataset) &&
    forecastWeatherStationId > 0 &&
    _option.WeatherStationId === forecastWeatherStationId &&
    _dataset.weatherStationId === forecastWeatherStationId &&
    _dataset.SensorTypeId === DATASET_CONFIG.WxNetIssuedForecastSensorTypeId // -2
  );
};

export const getMaxDataRange = (dsNum) => {
  if (!dsNum || dsNum <= 0) return DATA_RANGE.MAXIMUM;
  if (dsNum >= DATA_RANGE.MAXDATAUNIT) return 1;
  return Math.floor(DATA_RANGE.MAXDATAUNIT / dsNum);
};
// preset: graph preset.DataGraphs
export const countGraphPresetDatasets = (preset) => {
  let count = 0;
  if (!preset) return count;
  if (preset.GraphPresetId === DEFAULT_PRESET_ID_FOR_ALL_DATASETS) return count;
  if (Array.isArray(preset.DataGraphs)) {
    for (const dataGraph of preset.DataGraphs) {
      if (Array.isArray(dataGraph.PersistentGraphFilters)) {
        count += dataGraph.PersistentGraphFilters.length;
      }
    }
  }

  return count;
};
